import Model, { belongsTo, hasMany, attr } from '@ember-data/model';
import TaskStatuses from 'eflex/constants/task-statuses';
import { isBlank, isPresent } from '@ember/utils';
import getJemStatusTranslationOrUnknown from 'eflex/util/get-jem-status-translation-or-unknown';
import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';
import { getCaptionText } from 'eflex/util/translation-helper';
import { objectAttr, arrayAttr } from 'eflex/decorators';
import { compact, sortByProps } from 'ramda-adjunct';
import { chain, filter, flow, prop, reject, map } from 'ramda';
import { dp, intoArray } from '@eflexsystems/ramda-helpers';

const STARTED_STATUSES = new Set([TaskStatuses.STARTED, TaskStatuses.NEEDS_MULTIPIECE_QUANTITY]);

export default class BuildStatus extends Model {
  @attr('string') serialNumber;
  @attr('number') status;
  @attr('number') cycleTime;
  @attr('number') startTime;
  @attr('number') endTime;
  @attr('number', { defaultValue: 0 }) totalTargetCycleTime;
  @attr('number', { defaultValue: 0 }) palletNumber;
  @attr('number', { defaultValue: 0 }) bufferedDuration;
  @attr('number') goodQuantity;
  @attr('number') scrappedQuantity;
  @attr('number') rejectedQuantity;
  @attr('number') requiredQuantity;
  @attr('string') userId;
  @attr('string') userName;
  @attr('string') kineticEmployeeId;
  @attr('string') kineticEmployeeName;
  @attr('string') bomSourceLookupValue;
  @attr('string') area;
  @attr('string') multipieceId;
  @attr('date') timestamp;
  @attr('date') createdAt;
  @attr('boolean', { defaultValue: false }) isMultipiece;
  @arrayAttr allSerialNumbers;
  @arrayAttr marriedSerialNumbers;
  @arrayAttr previousDecisionTags;
  @objectAttr location;
  @objectAttr model;
  @objectAttr rejectCodes;
  @objectAttr scrapCodes;
  @objectAttr kineticJob;
  @objectAttr kineticJobAssembly;
  @objectAttr kineticAssemblyOperation;
  @objectAttr kineticOperation;
  @objectAttr kineticPart;
  @objectAttr kineticPartRev;

  @hasMany('build-status-child', { async: false, inverse: 'buildStatus', embedded: true }) children;
  @hasMany('custom-identifier-datum', { inverse: 'buildStatus', async: false, embedded: true }) customIdentifierData;
  @belongsTo('build-datum', { async: false, inverse: null }) buildDatum;
  @belongsTo('production-schedule', { async: false, inverse: null }) productionSchedule;
  @belongsTo('configuration-history', { async: false, inverse: null }) configurationHistory;
  @belongsTo('build-status-multipiece', { async: false, inverse: 'buildStatuses' }) buildStatusMultipiece;

  @tracked isSelected;

  @service intl;
  @service currentUser;

  @cached
  get sortedChildren() {
    return flow(this.children ?? [], [
      sortByProps([dp('location.row'), dp('location.column')]),
      compact,
    ]);
  }

  @cached
  get station() {
    if (!this.stationId) {
      return null;
    }

    return this.store.peekRecord('station', this.stationId) ?? this.location;
  }

  set station(station) {
    this.location = station;
  }

  get stationName() {
    return getCaptionText(this.station?.captions, this.currentUser);
  }

  get stationId() {
    return this.location?.id ?? this.location?._id;
  }

  get kineticAssemblyOperationId() {
    return this.kineticAssemblyOperation?._id ?? this.kineticAssemblyOperation?.id;
  }

  get kineticAssemblyOperationRecord() {
    const kineticAssemblyOperationId = this.kineticAssemblyOperationId;

    if (!kineticAssemblyOperationId) {
      return null;
    }

    return this.store.peekRecord('kinetic-assembly-operation', kineticAssemblyOperationId);
  }

  get kineticOperationRecord() {
    return this.kineticAssemblyOperationRecord?.operation;
  }

  get modelName() {
    let displayed = this.model?.code;
    const modelName = this.model?.name;

    if (!isBlank(modelName)) {
      displayed += ` - ${modelName}`;
    }

    return displayed;
  }

  get isStarted() {
    return STARTED_STATUSES.has(this.status);
  }

  get isRepaired() {
    return this.status === TaskStatuses.REPAIRED;
  }

  get usesComponents() {
    return this.station?.usesComponents ?? false;
  }

  get hasRejectCodes() {
    return isPresent(this.rejectCodes?.codes);
  }

  get hasScrapCodes() {
    return isPresent(this.scrapCodes?.codes);
  }

  get hasCodes() {
    return this.hasRejectCodes || this.hasScrapCodes;
  }

  get isGood() {
    return TaskStatuses.isGood(this.status);
  }

  get isRejected() {
    return TaskStatuses.isRejected(this.status);
  }

  get isContained() {
    return this.status === TaskStatuses.CONTAINED;
  }

  get hasCompletedTasks() {
    if (!this.children) {
      return false;
    }
    const validChildren = this.children.filter(child => child.status !== TaskStatuses.NOT_REQUIRED);

    return validChildren.some(child => child.isFinished);
  }

  get statusTranslation() {
    return getJemStatusTranslationOrUnknown(this.intl, `status.${this.status}`, this.status);
  }

  get statusClass() {
    if (this.isGood) {
      return 'bg-success';
    } else if (this.isRejected) {
      return 'bg-danger';
    } else if (this.isStarted) {
      return 'bg-primary';
    } else if (this.isContained) {
      return 'bg-warning';
    } else {
      return 'bg-gray-800';
    }
  }

  get formattedProcessData() {
    return this.customIdentifierData
      .map(identifier => ({
        timestamp: this.timestamp,
        displayName: identifier.name,
        displayValue: identifier.value,
      }))
      .concat(
        this.children.flatMap(buildStatusChild => buildStatusChild.formattedProcessData),
      );
  }

  get hasMarriages() {
    return this.children.some(item => item.latestPartMarriageStatus);
  }

  get formattedMarriages() {
    return flow(this.children ?? [], [
      chain(prop('allFormattedMarriages')),
      sortByProps(['timestamp']),
      compact,
    ]);
  }

  get formattedUploadedFileData() {
    return flow(this.children ?? [], [
      filter((child) => child.uploadedData?.fileUrl),
      map((child) => {
        return {
          taskName: child.taskName,
          fileName: child.uploadedData.fileName,
          fileUrl: child.uploadedData.fileUrl,
          timestamp: child.timestamp,
          isExpired: child.uploadedData.isExpired,
        };
      }),
      sortByProps(['timestamp']),
    ]);
  }

  get formattedScrapCodes() {
    if (!this.hasScrapCodes) {
      return '';
    }

    return this.scrapCodes.codes.map(({ code, description }) => {
      return `${code}: ${description}`;
    }).join(', ');
  }

  get formattedRejectCodes() {
    if (!this.hasRejectCodes) {
      return '';
    }

    return this.rejectCodes.codes.map(({ code, description }) => {
      return `${code}: ${description}`;
    }).join(', ');
  }

  get multipieceQuantities() {
    if (!this.isMultipiece) {
      return '';
    }

    const completedQuantity = this.buildStatusMultipiece?.completedQuantity;
    const requiredQuantity = this.buildStatusMultipiece?.requiredQuantity;

    if (requiredQuantity == null) {
      return '-';
    }

    return `${completedQuantity ?? 0}/${requiredQuantity}`;
  }

  getAllowedDecisions(task) {
    if (task == null) {
      return this.previousDecisionTags;
    }

    const currentDecisionTags = intoArray(
      filter(prop('isFinished')),
      reject(child => {
        if (child.task.row === task.row) {
          return child.task.column > task.column;
        } else {
          return child.task.row > task.row;
        }
      }),
      chain(prop('selectedDecisions')),
    )(this.children);

    return this.previousDecisionTags.concat(currentDecisionTags);
  }
}
